import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Trash2, PackageOpen, Plus } from "lucide-react";

const STATUS_TONE = {
  open: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  unclaimed: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  matched: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  returned: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  disposed: "border-white/10 text-zinc-400",
};

export default function LostFoundAdminPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ type: "found", item: "", location: "", notes: "" });

  const load = useCallback(async () => {
    if (!propertyId) return;
    const { data } = await api.get(`/lost-found?property_id=${propertyId}`);
    setItems(data.items || []);
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ type: "found", item: "", location: "", notes: "" });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.item.trim() || !form.location.trim()) {
      return toast.error("Item + location are required");
    }
    try {
      await api.post(`/lost-found?property_id=${propertyId}`, {
        type: form.type,
        item: form.item.trim(),
        location: form.location.trim(),
      });
      toast.success(form.type === "lost" ? "Lost item reported" : "Found item logged");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/lost-found/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item from lost & found?")) return;
    try {
      await api.delete(`/admin/lost-found/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const lost = items.filter((i) => i.type === "lost");
  const found = items.filter((i) => i.type === "found");

  return (
    <div>
      <PageHeader
        eyebrow="Community"
        title="Lost & Found"
        subtitle="Match lost items with found items, mark returned, or dispose the stale ones."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">{lost.length} lost</Badge>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">{found.length} found</Badge>
            <Button size="sm" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 gap-2" data-testid="lf-new-btn">
              <Plus className="h-4 w-4" /> Log item
            </Button>
          </div>
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10" data-testid="lf-dialog">
          <DialogHeader><DialogTitle>Log lost or found item</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="lf-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="found">Found (turned in at front desk)</SelectItem>
                  <SelectItem value="lost">Lost (resident report)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Item description</Label>
              <Input data-testid="lf-form-item" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="Black wallet, silver iPhone, red umbrella…" />
            </div>
            <div>
              <Label>Location {form.type === "lost" ? "last seen" : "found"}</Label>
              <Input data-testid="lf-form-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Pool deck, lobby, gym locker room…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={save} data-testid="lf-form-save">Log</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="divide-y divide-white/5">
          {items.map((i) => (
            <div key={i.id} className="px-4 py-3 flex items-center gap-4" data-testid={`lf-${i.id}`}>
              <div className={"h-9 w-9 rounded-md border flex items-center justify-center shrink-0 " +
                (i.type === "lost" ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                                   : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300")}>
                <Search className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="border-white/10 capitalize">{i.type}</Badge>
                  <Badge variant="outline" className={STATUS_TONE[i.status] || "border-white/10"}>{i.status}</Badge>
                  <span className="font-medium truncate">{i.item}</span>
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">📍 {i.location} · Reported by {i.reported_by} · {new Date(i.created_at).toLocaleString()}</div>
              </div>
              <Select value={i.status} onValueChange={(v) => setStatus(i.id, v)}>
                <SelectTrigger className="w-40 bg-white/[0.02] border-white/10 h-8 text-xs" data-testid={`lf-status-${i.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="unclaimed">Unclaimed</SelectItem>
                  <SelectItem value="matched">Matched</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                  <SelectItem value="disposed">Disposed</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300" onClick={() => remove(i.id)} data-testid={`lf-delete-${i.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
          {items.length === 0 && (
            <div className="px-6 py-12 text-center text-muted-foreground">
              <PackageOpen className="h-6 w-6 mx-auto mb-2 opacity-60" />
              Nothing reported.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
